import { Link, useSearch } from "@tanstack/react-router";
import { DIAS_POR_VENCER, formatearMoneda, type FiltroVencimiento } from "@uirtus/shared";
import { useState } from "react";
import { EstadoDominio, formatearFechaCorta } from "../componentes/estado-dominio";
import { FormularioDominio } from "../componentes/formulario-dominio";
import { GestorProveedores } from "../componentes/gestor-proveedores";
import { Aviso, Boton, Entrada, Etiqueta, Modal, Seleccion } from "../componentes/ui";
import { ErrorApi } from "../lib/api";
import {
  useCorreoDominios,
  useCrearDominio,
  useDominios,
  useProveedoresDominio,
  useResumenDominios,
} from "../lib/dominios";
import { useDebounce } from "../lib/use-debounce";

const opcionesVencimiento = (dias: number): { valor: FiltroVencimiento; texto: string }[] => [
  { valor: "todos", texto: "Todos los vencimientos" },
  { valor: "vencidos", texto: "Vencidos" },
  { valor: "por_vencer", texto: `Vencen en ${dias} días o menos` },
  { valor: "vigentes", texto: `Vigentes (más de ${dias} días)` },
];

export function Dominios() {
  const [texto, setTexto] = useState("");
  const inicial = useSearch({ from: "/dominios" });
  const [vencimiento, setVencimiento] = useState<FiltroVencimiento>(
    inicial.vencimiento ?? "todos",
  );
  const dias = inicial.dias ?? DIAS_POR_VENCER;
  const [proveedorId, setProveedorId] = useState("");
  const [verBajas, setVerBajas] = useState(false);
  const [creando, setCreando] = useState(false);
  const [proveedoresAbierto, setProveedoresAbierto] = useState(false);
  const busqueda = useDebounce(texto, 300);

  const { data, isLoading, isError, error } = useDominios({
    q: busqueda || undefined,
    vencimiento,
    dias,
    proveedorId: proveedorId || undefined,
    activo: verBajas ? "todos" : "true",
  });
  const { data: proveedores } = useProveedoresDominio();
  const { data: resumen } = useResumenDominios();
  const { data: correo } = useCorreoDominios();
  const crear = useCrearDominio();
  const dominios = data ?? [];

  const hayFiltros = !!(busqueda || proveedorId || vencimiento !== "todos");

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Dominios</h1>
          <p className="text-sm text-slate-500">
            Todos los dominios que administras, sin importar dónde estén contratados.
          </p>
        </div>
        <div className="flex gap-2">
          <Boton variante="secundario" onClick={() => setProveedoresAbierto(true)}>
            Proveedores
          </Boton>
          <Boton onClick={() => setCreando(true)}>Nuevo dominio</Boton>
        </div>
      </div>

      {resumen && resumen.total > 0 && (
        <div className="grid gap-3 sm:grid-cols-4">
          <Contador titulo="Administrados" valor={resumen.total} />
          <Contador titulo="Vencidos" valor={resumen.vencidos} tono="peligro" />
          <Contador titulo="Vencen en 30 días" valor={resumen.vencenEn30} tono="alerta" />
          <Contador titulo="Vencen en 60 días" valor={resumen.vencenEn60} tono="alerta" />
        </div>
      )}

      {correo && !correo.habilitado && (
        <Aviso tono="alerta">
          El envío de correos no está configurado en el servidor (variable{" "}
          <code className="font-mono">SMTP_HOST</code>), así que los recordatorios
          automáticos <strong>no salen</strong>. Los vencimientos se ven aquí igual, y desde
          cada dominio puedes redactar el correo para mandarlo tú.
        </Aviso>
      )}

      <div className="flex flex-wrap gap-2">
        <Entrada
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Buscar dominio, cuenta o nota"
          className="w-full sm:w-72"
        />
        <Seleccion
          value={vencimiento}
          onChange={(e) => setVencimiento(e.target.value as FiltroVencimiento)}
          className="w-full sm:w-56"
        >
          {opcionesVencimiento(dias).map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.texto}
            </option>
          ))}
        </Seleccion>
        <Seleccion
          value={proveedorId}
          onChange={(e) => setProveedorId(e.target.value)}
          className="w-full sm:w-48"
        >
          <option value="">Todos los proveedores</option>
          {(proveedores ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.nombre}
            </option>
          ))}
        </Seleccion>
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
          <input
            type="checkbox"
            checked={verBajas}
            onChange={(e) => setVerBajas(e.target.checked)}
            className="rounded border-slate-300 text-marca-600 focus:ring-marca-500"
          />
          Incluir dados de baja
        </label>
      </div>

      {isError && (
        <Aviso>{error instanceof ErrorApi ? error.message : "No se pudo cargar la lista"}</Aviso>
      )}

      {isLoading ? (
        <p className="text-sm text-slate-500">Cargando…</p>
      ) : isError ? null : dominios.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 px-4 py-12 text-center dark:border-slate-700">
          <p className="text-sm text-slate-500">
            {hayFiltros
              ? "Ningún dominio coincide con los filtros."
              : "Todavía no hay dominios registrados."}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800">
              <tr>
                <th className="px-4 py-2">Dominio</th>
                <th className="px-4 py-2">Cliente</th>
                <th className="px-4 py-2">Proveedor</th>
                <th className="px-4 py-2">Vencimiento</th>
                <th className="px-4 py-2 text-right">Renovación</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {dominios.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="px-4 py-2">
                    <Link
                      to="/dominios/$id"
                      params={{ id: d.id }}
                      className="font-mono font-medium text-marca-600 hover:underline dark:text-marca-500"
                    >
                      {d.nombre}
                    </Link>
                    <div className="mt-0.5 flex gap-1">
                      {d.autoRenovacion && <Etiqueta>Auto-renueva</Etiqueta>}
                      {!d.recordatoriosActivos && <Etiqueta tono="alerta">Sin avisos</Etiqueta>}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div>{d.cliente.razonSocial}</div>
                    <div className="font-mono text-xs text-slate-500">{d.cliente.rfc}</div>
                  </td>
                  <td className="px-4 py-2 text-slate-600 dark:text-slate-300">
                    {d.proveedor?.nombre ?? "—"}
                  </td>
                  <td className="px-4 py-2">
                    <div>{formatearFechaCorta(d.fechaVencimiento)}</div>
                    <EstadoDominio dias={d.diasRestantes} activo={d.activo} />
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {d.costoRenovacion ? formatearMoneda(d.costoRenovacion, d.moneda) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        titulo="Nuevo dominio"
        abierto={creando}
        alCerrar={() => {
          setCreando(false);
          crear.reset();
        }}
      >
        <FormularioDominio
          enviando={crear.isPending}
          errorServidor={crear.error ? crear.error.message : null}
          alEnviar={(datos) =>
            crear.mutate(datos as never, {
              onSuccess: () => {
                setCreando(false);
                crear.reset();
              },
            })
          }
          alCancelar={() => {
            setCreando(false);
            crear.reset();
          }}
        />
      </Modal>

      <GestorProveedores
        abierto={proveedoresAbierto}
        alCerrar={() => setProveedoresAbierto(false)}
      />
    </div>
  );
}

function Contador({
  titulo,
  valor,
  tono,
}: {
  titulo: string;
  valor: number;
  tono?: "alerta" | "peligro";
}) {
  const color =
    valor > 0 && tono === "peligro"
      ? "text-red-600"
      : valor > 0 && tono === "alerta"
        ? "text-amber-600"
        : "";
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-xs uppercase text-slate-500">{titulo}</p>
      <p className={`mt-0.5 text-xl font-semibold ${color}`}>{valor}</p>
    </div>
  );
}
